/**
 * What an attachment may be drawn as, and the page's cache of what has been.
 *
 * ── The type is a stranger's word ───────────────────────────────────────────
 * The sender writes `m`, and Cloud's copy is whatever the uploader's browser
 * said. So a type is either on the list below, and drawn as that type and no
 * other — the `Blob` is made with the listed type, never the one that came —
 * or the file stays a card. Nothing is sniffed. SVG and HTML are the two that
 * matter most to keep off: both are documents that run script when opened as
 * one, and a blob URL *can* be opened as a page — somebody can open an image or
 * a video in a tab of its own. So the list is the whole defence there, and
 * every type on it that is made into a `Blob` is an image, a video or a sound.
 * Text is decoded to a string and drawn as a string child, never as HTML and
 * never through a blob URL.
 *
 * ── Caps ────────────────────────────────────────────────────────────────────
 * Images to 20 MB and text to 256 KB load by themselves as they near the
 * screen, the first four in a message; past four they wait for a click. Video
 * and sound to 150 MB load when somebody presses play. Past its cap a file is
 * a card; the bridge holds Cloud to the same cap whatever the message claimed
 * the size was, and this holds the bridge to it. At most two loads run at once
 * for the page, and the rest wait their turn.
 *
 * ── The cache is keyed by the link ──────────────────────────────────────────
 * The node id is the sender's word too. Keyed by it, one message could draw its
 * own bytes in another message's row by naming that row's node. The link's
 * secret is what decides what the bytes are, so it is the key, and the only
 * key: the type is the sender's word as well, so one link named as many types
 * is still one load. Each row draws the bytes as the listed type it named.
 * One cache for the page, shared by the transcript and the thread pane, of
 * about 200 MB; the least recently drawn goes first, and its object URLs are
 * revoked as it goes. A refusal is remembered for the page too, so a link that
 * will not open is not asked for again on every scroll. A timeout is not: the
 * next time the row is drawn, it asks again.
 */

import { cloudBridge, type Link } from './cloud-bridge';
import type { Attachment } from './types';

export type PreviewKind = 'image' | 'video' | 'audio' | 'text';

const MB = 1024 * 1024;
export const MAX_AUTO_IMAGE_BYTES = 20 * MB;
export const MAX_AUTO_TEXT_BYTES = 256 * 1024;
export const MAX_MEDIA_BYTES = 150 * MB;
export const CACHE_BYTES = 200 * MB;
/** How many attachments in one message load without a click. */
export const MAX_AUTO_PER_MESSAGE = 4;
/** How many loads the page runs through the bridge at once. */
export const MAX_IN_FLIGHT = 2;

const CAPS: Record<PreviewKind, number> = {
  image: MAX_AUTO_IMAGE_BYTES,
  video: MAX_MEDIA_BYTES,
  audio: MAX_MEDIA_BYTES,
  text: MAX_AUTO_TEXT_BYTES
};

// The whole list. A type not here is a card.
const KINDS = new Map<string, PreviewKind>([
  ['image/png', 'image'],
  ['image/jpeg', 'image'],
  ['image/gif', 'image'],
  ['image/webp', 'image'],
  ['image/avif', 'image'],
  ['video/mp4', 'video'],
  ['video/webm', 'video'],
  ['audio/mpeg', 'audio'],
  ['audio/mp4', 'audio'],
  ['audio/aac', 'audio'],
  ['audio/ogg', 'audio'],
  ['audio/wav', 'audio'],
  ['audio/webm', 'audio'],
  ['text/plain', 'text'],
  ['text/markdown', 'text'],
  ['text/csv', 'text'],
  ['application/json', 'text'],
  ['text/javascript', 'text'],
  ['text/css', 'text'],
  ['text/x-python', 'text'],
  ['application/x-sh', 'text'],
  ['text/yaml', 'text']
]);

// Other names for listed types, as browsers and operating systems write them.
const ALIASES = new Map<string, string>([
  ['image/jpg', 'image/jpeg'],
  ['audio/mp3', 'audio/mpeg'],
  ['audio/x-m4a', 'audio/mp4'],
  ['audio/x-wav', 'audio/wav'],
  ['audio/wave', 'audio/wav'],
  ['text/x-markdown', 'text/markdown'],
  ['application/javascript', 'text/javascript'],
  ['text/x-sh', 'application/x-sh'],
  ['application/yaml', 'text/yaml'],
  ['application/x-yaml', 'text/yaml'],
  ['text/x-yaml', 'text/yaml']
]);

/** The listed type `mime` names, or null. Case and parameters are dropped. */
export function previewType(mime: string): string | null {
  const bare = (mime.split(';')[0] ?? '').trim().toLowerCase();
  const type = ALIASES.get(bare) ?? bare;
  return KINDS.has(type) ? type : null;
}

// The link in an address `payload.ts` or `cloud-picker.ts` built, each from
// halves held to these same shapes.
const LINK = /\/s\/([A-Za-z0-9_-]{1,64})#([A-Za-z0-9_-]{43})$/;

export interface Preview {
  kind: PreviewKind;
  /** The listed type: what the bytes are drawn as. */
  type: string;
  link: Link;
  /** What the bridge is asked to refuse past. */
  maxBytes: number;
}

/** How `file` may be drawn, or null for the card. */
export function previewOf(file: Attachment): Preview | null {
  const match = LINK.exec(file.cloudUrl);
  if (!match) return null;
  const type = previewType(file.mime);
  if (!type) return null;
  const kind = KINDS.get(type)!;
  const maxBytes = CAPS[kind];
  if (file.bytes > maxBytes) return null;
  return { kind, type, link: { id: match[1]!, secret: match[2]! }, maxBytes };
}

/**
 * Which of one message's attachments load by themselves: the first four that
 * would. Video and sound never do, so they are not counted.
 */
export function autoLoads(files: Attachment[]): boolean[] {
  let left = MAX_AUTO_PER_MESSAGE;
  return files.map((f) => {
    const kind = previewOf(f)?.kind;
    return (kind === 'image' || kind === 'text') && left-- > 0;
  });
}

/** The start of `text`, to draw before "Show all". */
export function clipText(text: string, maxLines = 12, maxChars = 1500): { text: string; clipped: boolean } {
  const shown = text.split('\n').slice(0, maxLines).join('\n').slice(0, maxChars);
  return { text: shown, clipped: shown.length < text.length };
}

/** Loaded files, least recently used first, each released as it goes. */
export class PreviewCache<T> {
  private readonly entries = new Map<string, { value: T; size: number }>();
  private total = 0;
  private readonly budget: number;
  private readonly release: (value: T) => void;

  constructor(budget: number, release: (value: T) => void) {
    this.budget = budget;
    this.release = release;
  }

  get bytes(): number {
    return this.total;
  }

  get(key: string): T | undefined {
    const entry = this.entries.get(key);
    if (!entry) return undefined;
    this.entries.delete(key);
    this.entries.set(key, entry);
    return entry.value;
  }

  put(key: string, value: T, size: number): void {
    this.drop(key);
    this.entries.set(key, { value, size });
    this.total += size;
    for (const oldest of this.entries.keys()) {
      if (this.total <= this.budget || oldest === key) break;
      this.drop(oldest);
    }
  }

  private drop(key: string): void {
    const entry = this.entries.get(key);
    if (!entry) return;
    this.entries.delete(key);
    this.total -= entry.size;
    this.release(entry.value);
  }
}

/** One link's bytes, and what they have been drawn as. */
export interface Loaded {
  /** Typeless; each draw takes a typed slice of it, which copies nothing. */
  blob: Blob;
  /** By listed type: an object URL, or the decoded text. */
  drawn: Map<string, string>;
  /** Every object URL in `drawn`, to revoke with it. */
  urls: string[];
}

export interface PreviewLoader {
  /** The drawn value; null when it failed on this page; undefined when neither yet. */
  peek(preview: Preview): string | null | undefined;
  /** The drawn value, loading it once however many rows ask. */
  load(preview: Preview): Promise<string>;
}

export function createLoader(
  bridge: () => { open(link: Link, maxBytes: number): Promise<{ bytes: ArrayBuffer }> },
  cache: PreviewCache<Loaded>,
  makeUrl: (blob: Blob) => string,
  maxInFlight = MAX_IN_FLIGHT
): PreviewLoader {
  const loading = new Map<string, Promise<Loaded>>();
  const failed = new Set<string>();
  const queue: (() => void)[] = [];
  let running = 0;

  /** `task` once fewer than `maxInFlight` are running. */
  const inTurn = <T>(task: () => Promise<T>): Promise<T> =>
    new Promise<T>((resolve, reject) => {
      const run = () => {
        running += 1;
        task()
          .then(resolve, reject)
          .finally(() => {
            running -= 1;
            queue.shift()?.();
          });
      };
      if (running < maxInFlight) run();
      else queue.push(run);
    });

  /** The bytes as the type `preview` names, held to its own cap. */
  async function draw(loaded: Loaded, preview: Preview): Promise<string> {
    if (loaded.blob.size > preview.maxBytes) throw new Error('too-large');
    let value = loaded.drawn.get(preview.type);
    if (value === undefined) {
      if (preview.kind === 'text') {
        value = await loaded.blob.text();
      } else {
        value = makeUrl(loaded.blob.slice(0, loaded.blob.size, preview.type));
        loaded.urls.push(value);
      }
      loaded.drawn.set(preview.type, value);
    }
    return value;
  }

  return {
    peek(preview) {
      const key = preview.link.secret;
      return failed.has(key) ? null : cache.get(key)?.drawn.get(preview.type);
    },

    load(preview) {
      const key = preview.link.secret;
      const hit = cache.get(key);
      if (hit) return draw(hit, preview);
      if (failed.has(key)) return Promise.reject(new Error('failed'));

      let pending = loading.get(key);
      if (!pending) {
        pending = inTurn(() => bridge().open(preview.link, preview.maxBytes))
          .then(({ bytes }) => {
            if (bytes.byteLength > preview.maxBytes) throw new Error('too-large');
            const loaded: Loaded = { blob: new Blob([bytes]), drawn: new Map(), urls: [] };
            cache.put(key, loaded, bytes.byteLength);
            return loaded;
          })
          .catch((err: unknown) => {
            // A refusal is the link's answer, and will be the same next time.
            // A timeout is the network's, and may not be.
            if (!(err instanceof Error && err.message === 'timeout')) failed.add(key);
            throw err;
          })
          .finally(() => loading.delete(key));
        loading.set(key, pending);
      }
      return pending.then((loaded) => draw(loaded, preview));
    }
  };
}

/** The page's previews: one cache, one bridge. */
export const previews = createLoader(
  cloudBridge,
  new PreviewCache<Loaded>(CACHE_BYTES, (loaded) => loaded.urls.forEach((url) => URL.revokeObjectURL(url))),
  (blob) => URL.createObjectURL(blob)
);
